import Modal from 'uilib-wrappers/modal';
import Button from 'uilib-wrappers/button';
import Link from 'uilib-wrappers/link';
import Text from 'uilib-wrappers/text';
import Spacer from '@uilib/core/Spacer/Spacer';
import Progress from 'Bricks/Progress';
import Theme from '@uilib/business-components/_utils/theme';

import { createProtectTransitionKBLink } from 'app.common';
import { BACKEND, withBackend } from 'Services/backend';
import { useEffect, useState, Fragment } from 'react';
import moment from 'moment';

const SETTINGS_URL = 'users/settings/protectMigrationModal';
const MIGRATION_START_PROPERTY = 'config/protectMigrationStart';
const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const REPEAT_INTERVAL = { value: 1, unit: 'weeks' };

const PROTECT_MIGRATION_MODAL_THEME = {
    Modal: {
        maxHeight: 'min(808px, 90%)',
        maxWidth: 'min(1239px, 75%)',
    },
};

function ProtectMigrationModal(props) {
    const [modalOpen, setModalOpen] = useState(false);
    const [showProgress, setShowProgress] = useState(false);

    function parseUtcDate(value) {
        if (typeof value !== 'string' || !value) {
            return null;
        }
        const parsed = moment.utc(value, DATE_FORMAT);
        return parsed.isValid() ? parsed : null;
    }

    function shouldShow(scheduledTime) {
        // First display for user
        if (!scheduledTime) {
            return true;
        }
        const nextShow = parseUtcDate(scheduledTime);
        return !!nextShow && moment.utc().isSameOrAfter(nextShow);
    }

    function showModalOnDemand() {
        BACKEND.get(SETTINGS_URL, props.componentUuid)
            .onStatus(404, () => Promise.resolve(null))
            .success((response) => {
                if (shouldShow(response?.scheduledTime)) {
                    setModalOpen(true);
                } else {
                    props.onExit();
                }
            })
            .failure(() => props.onExit())
            .always(() => setShowProgress(false))
            .execute();
    }

    useEffect(() => {
        if (!props.open || modalOpen) {
            return;
        }

        setShowProgress(true);
        BACKEND.get(MIGRATION_START_PROPERTY, props.componentUuid)
            .onStatus(404, () => Promise.resolve(null))
            .success((migrationStart) => {
                // Show only while the config is set (a valid start date)
                const start = parseUtcDate(migrationStart);
                if (!!start && moment.utc().isSameOrAfter(start)) {
                    showModalOnDemand();
                    return;
                }
                setShowProgress(false);
                props.onExit();
            })
            .failure(() => {
                setShowProgress(false);
                props.onExit();
            })
            .execute();
    }, [props.open]);

    function handleClose() {
        // Schedule the next reminder and anchored to 6 AM
        const scheduledTime = moment()
            .add(REPEAT_INTERVAL.value, REPEAT_INTERVAL.unit)
            .set({ hour: 6, minute: 0, second: 0, millisecond: 0 })
            .utc()
            .format(DATE_FORMAT);

        BACKEND.put(SETTINGS_URL, { scheduledTime }, props.componentUuid)
            .always(() => setModalOpen(false))
            .execute();
    }

    function handleMoreInfo() {
        window.open(createProtectTransitionKBLink(), '_blank', 'noopener,noreferrer');
    }

    return (
        <Fragment>
            <Progress showProgress={showProgress} />
            <Theme.Provider theme={PROTECT_MIGRATION_MODAL_THEME}>
                <Modal
                    id="eid-protect-migration"
                    show={modalOpen}
                    title="PROTECT_MIGRATION_MODAL_TITLE"
                    onDispose={handleClose}
                    onHidden={props.onExit}
                    buttons={[
                        <Button id="eid-protect-migration-confirm" type="primary" text="OK" onClick={handleClose} />,
                    ]}
                >
                    <Fragment>
                        <Text>PROTECT_MIGRATION_MODAL_DESCRIPTION</Text>
                        <Spacer type="pt-4">
                            <Link onClick={handleMoreInfo} text="MORE_INFORMATION" />
                        </Spacer>
                    </Fragment>
                </Modal>
            </Theme.Provider>
        </Fragment>
    );
}

export default withBackend(ProtectMigrationModal);
